<template>
  <head>
    <meta charset="utf-8"/>
    <title>Desk Birdy | Business Permits</title>
    <meta content="width=device-width, initial-scale=1.0" name="viewport">
    <meta content="Rental management system by Nouveta LTD" name="description"/>
    <meta content="Kelvin Njuguna" name="author"/>
    <!-- App favicon -->
    <link href="assets/images/favicon.svg" rel="shortcut icon">
    <!-- DataTables -->
    <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css"/>
    <!-- Bootstrap Css -->
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css"/>
    <!-- Bootstrap select styling -->
    <link href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css" rel="stylesheet">
    <!-- Icons Css -->
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css"/>
    <!-- App Css-->
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css"/>
    <!-- custom Css-->
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css"/>
  </head>

  <body @contextmenu.prevent data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <NavigationBar/>

    <!-- Start right Content here -->
    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page title with duplicate manager button -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Business Permits</h4>
                <div class="page-title-right d-flex align-items-center">
                  <!-- Simple Duplicate Manager button -->
                  <button
                      @click="openDuplicateManager"
                      class="btn btn-warning me-3"
                      v-if="getRight('MANAGE DUPLICATES')"
                  >
                    <i class="bx bx-duplicate me-2"></i>
                    Duplicate Manager
                  </button>

                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="index.html">Dashboards</a></li>
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Certificates & Licenses</a></li>
                    <li class="breadcrumb-item"><a href="cert-biz-stats.html">Business Permits</a></li>
                    <li class="breadcrumb-item active">Applications Pending Validation</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <ToolBar/>

          <!-- Main content -->
          <div class="row">
            <div class="col-12">
              <div class="card">

                <!-- Location & Business Type Filters -->
                <div class="card-header bg-light border-bottom">
                  <div class="row g-3 align-items-end">
                    <!-- SubCounty Filter -->
                    <div class="col-md-2">
                      <label class="form-label fw-semibold mb-1">
                        <i class="bx bx-map me-1"></i>Sub County
                      </label>
                      <select
                          v-model="selectedSubCounty"
                          class="form-select"
                          @change="onSubCountyChange"
                      >
                        <option value="">All Sub Counties</option>
                        <option
                            v-for="subCounty in uniqueSubCounties"
                            :key="subCounty.subCountyID"
                            :value="subCounty.subCountyID"
                        >
                          {{ subCounty.subCountyName }}
                        </option>
                      </select>
                    </div>

                    <!-- Ward Filter -->
                    <div class="col-md-2">
                      <label class="form-label fw-semibold mb-1">
                        <i class="bx bx-map-pin me-1"></i>Ward
                      </label>
                      <select
                          v-model="selectedWard"
                          class="form-select"
                          @change="onWardChange"
                          :disabled="!selectedSubCounty"
                      >
                        <option value="">All Wards</option>
                        <option
                            v-for="ward in filteredWards"
                            :key="ward.wardID"
                            :value="ward.wardID"
                        >
                          {{ ward.wardName }}
                        </option>
                      </select>
                    </div>

                    <!-- Zone Filter -->
                    <div class="col-md-2">
                      <label class="form-label fw-semibold mb-1">
                        <i class="bx bx-target-lock me-1"></i>Zone
                      </label>
                      <select
                          v-model="selectedZone"
                          class="form-select"
                          @change="onZoneChange"
                          :disabled="!selectedWard"
                      >
                        <option value="">All Zones</option>
                        <option
                            v-for="zone in filteredZones"
                            :key="zone.id"
                            :value="zone.id"
                        >
                          {{ zone.name }}
                        </option>
                      </select>
                    </div>

                    <!-- Business Type Filter -->
                    <div class="col-md-3">
                      <label class="form-label fw-semibold mb-1">
                        <i class="bx bx-briefcase me-1"></i>Business Type
                      </label>
                      <select
                          v-model="selectedBusinessDes"
                          class="form-select"
                          @change="onBusinessDesChange"
                      >
                        <option value="">All Business Types</option>
                        <option
                            v-for="(businessDes, index) in businessDesOptions"
                            :key="index"
                            :value="businessDes"
                        >
                          {{ businessDes }}
                        </option>
                      </select>
                    </div>

                    <!-- Clear Filters Button -->
                    <div class="col-md-3">
                      <button
                          @click="clearFilters"
                          class="btn btn-outline-secondary w-100"
                          :disabled="!hasActiveFilters"
                      >
                        <i class="bx bx-x me-1"></i>Clear Filters
                      </button>
                    </div>
                  </div>

                  <!-- Active Filters Badges -->
                  <div class="mt-3" v-if="hasActiveFilters">
                    <span class="badge bg-light text-primary me-2" v-if="selectedSubCounty">
                      <i class="bx bx-map me-1"></i>{{ getSubCountyName(selectedSubCounty) }}
                    </span>
                    <span class="badge bg-light text-primary me-2" v-if="selectedWard">
                      <i class="bx bx-map-pin me-1"></i>{{ getWardName(selectedWard) }}
                    </span>
                    <span class="badge bg-light text-primary me-2" v-if="selectedZone">
                      <i class="bx bx-target-lock me-1"></i>{{ getZoneName(selectedZone) }}
                    </span>
                    <span class="badge bg-light text-primary me-2" v-if="selectedBusinessDes">
                      <i class="bx bx-briefcase me-1"></i>{{ selectedBusinessDes }}
                    </span>
                  </div>
                </div>

                <!-- Date and Category Filters -->
                <div class="card-header bg-white pt-0 pr-0 p-0 d-flex justify-content-between align-items-center w-100 border-bottom">
                  <div class="d-flex align-items-center justify-content-center p-3" role="toolbar">
                    <label class="text-nowrap mb-0 px-2">Date From:</label>
                    <input v-model="dateFrom" class="form-control pr-3" type="date">

                    <label class="text-nowrap mb-0 px-2">Date To:</label>
                    <input v-model="dateTo" class="form-control pr-2" type="date">

                    <label class="text-nowrap mb-0 px-2">Business Activity:</label>
                    <select @change="getTradeSubCategory($event)" class="form-control bg-white" data-live-search="true" title="Select Business Activity">
                      <option value="">All</option>
                      <option :key="index" :value="item.incomeTypeId" v-for="(item, index) in tradeCategories">
                        {{ item.incomeTypeDescription }}
                      </option>
                    </select>

                    <label class="text-nowrap mb-0 mx-2">Business Sub Category:</label>
                    <select @change="selectedTradeSubCategory($event)" class="form-control bg-white" data-live-search="true" title="Select Sub Categories">
                      <option :key="index" :value="JSON.stringify(item)" v-for="(item, index) in tradeSubCategories">
                        {{ item.feeDescription }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Search and Download -->
                <div class="card-header bg-white pt-0 pr-0 p-0 d-flex justify-content-between align-items-center w-100 border-bottom">
                  <div class="d-flex align-items-center justify-content-center p-3 flex-grow-1" role="toolbar">
                    <form class="app-search p-0 m-0 w-100">
                      <div class="position-relative">
                        <input v-model="search" type="text" class="form-control w-100" placeholder="Search... eg Business Name, ID Number, Bill Number, etc" spellcheck="false">
                        <span class="bx bx-search-alt"></span>
                      </div>
                    </form>
                  </div>

                  <div class="pr-3 d-flex gap-2">
                    <!-- Download Current Page -->
                    <button
                        v-if="getRight('DOWNLOAD REPORTS')"
                        @click="downloadCurrentPage()"
                        class="btn btn-success"
                        :disabled="businesses.length === 0 || isDownloading">
                      <i class="bx bxs-download font-size-16"></i>
                      <span class="ms-1">Download Page</span>
                    </button>

                    <!-- Download All Records -->
                    <button
                        v-if="getRight('DOWNLOAD REPORTS')"
                        @click="downloadAllRecords()"
                        class="btn btn-primary"
                        :disabled="isDownloadingAll || totalItems === 0">
                      <span v-if="isDownloadingAll">
                        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                        Downloading...
                      </span>
                      <span v-else>
                        <i class="bx bxs-cloud-download font-size-16"></i>
                        <span class="ms-1">Download All ({{ numberFormat(totalItems) }} records)</span>
                      </span>
                    </button>
                  </div>
                </div>

                <!-- Table -->
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table align-middle table-hover contacts-table table-striped" id="datatable-buttons">
                      <thead class="table-light">
                      <tr class="table-dark">
                        <th>#</th>
                        <th>Business ID</th>
                        <th>Business Name</th>
                        <th>Category</th>
                        <th>Sub Category</th>
                        <th>Business Type</th>
                        <th>Owner / Full Names</th>
                        <th>Sub County</th>
                        <th>Ward</th>
                        <th>Zone</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th>Physical Address</th>
                        <th>Plot No.</th>
                        <th>Postal Address</th>
                        <th>Postal Code</th>
                        <th>Latitude</th>
                        <th>Longitude</th>
                        <th>Fee ID</th>
                        <th>Income Type ID</th>
                        <th>Created By</th>
                        <th>Date Created</th>
                        <th class="text-center">Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr :key="index" v-for="(item, index) in businesses">
                        <td class="text-nowrap">{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                        <td class="text-nowrap">{{ item.businessID }}</td>
                        <td class="text-nowrap">
                          <a href="#">{{ item.businessName }}</a><br>
                          <small class="text-muted">{{ formattedDate(item.dateCreated) }}</small>
                        </td>
                        <td class="text-nowrap"><b>{{ item.businessCategory }}</b></td>
                        <td class="text-nowrap" style="max-width:220px; white-space:normal;">
                          <small>{{ item.businessSubCategory }}</small>
                        </td>
                        <td class="text-nowrap">{{ item.businessDes || '-' }}</td>
                        <td class="text-nowrap">{{ item.fullnames || '-' }}</td>
                        <td class="text-nowrap">{{ item.subCountyName }}</td>
                        <td class="text-nowrap">{{ item.wardName }}</td>
                        <td class="text-nowrap">{{ item.zone || '-' }}</td>
                        <td class="text-nowrap">{{ item.contactPersonPhone || '-' }}</td>
                        <td class="text-nowrap">{{ item.businessEmail || '-' }}</td>
                        <td class="text-nowrap">{{ item.physicalAddress || '-' }}</td>
                        <td class="text-nowrap">{{ item.plotNumber || '-' }}</td>
                        <td class="text-nowrap">{{ item.postalAddress || '-' }}</td>
                        <td class="text-nowrap">{{ item.postalCode || '-' }}</td>
                        <td class="text-nowrap">{{ item.lat || '-' }}</td>
                        <td class="text-nowrap">{{ item.lng || '-' }}</td>
                        <td class="text-nowrap">{{ item.feeID || '-' }}</td>
                        <td class="text-nowrap">{{ item.incomeTypeID || '-' }}</td>
                        <td class="text-nowrap">{{ item.createdBy }}</td>
                        <td class="text-nowrap">{{ formattedDate(item.dateCreated) }}</td>
                        <td class="text-center text-nowrap">
                          <button
                              @click="viewBusinessEntries(item)"
                              class="btn btn-sm btn-primary"
                              title="View Details"
                          >
                            <i class="bx bx-show"></i> View Details
                          </button>
                        </td>
                      </tr>
                      </tbody>
                      <tfoot class="table-dark">
                      <tr>
                        <th colspan="21">
                          {{ businesses.length }} Businesses
                          <span v-if="hasActiveFilters" class="text-warning ms-2">(filtered)</span>
                        </th>
                        <td class="text-nowrap text-right" colspan="2">
                          <span class="fw-semibold">KES {{ numberFormat(totalAmount) }}</span>
                        </td>
                      </tr>
                      </tfoot>
                    </table>

                    <!-- Loading state -->
                    <div class="card-body px-5 d-flex flex-column justify-items-center align-items-center text-center d-flex justify-content-center" v-if="loading">
                      <div class="p-5 py-0 pt-3">
                        <div class="p-5">
                          <div class="spinner-border text-info m-1" role="status">
                            <span class="sr-only text-uppercase">Loading...</span>
                          </div>
                        </div>
                      </div>
                      <h4 class="text-uppercase">Loading Data</h4>
                      <div class="col-sm-12 col-md-8 col-lg-5">
                        <p>We are fetching the data for you. It will be ready soon. Please wait and have fun. Thank you for your interest.</p>
                      </div>
                    </div>

                    <!-- No results -->
                    <div class="card-body d-sm-none d-md-block px-5 d-flex flex-column justify-items-center align-items-center text-center" v-if="businesses.length < 1 && loading === false">
                      <div class="p-5 py-0 pt-3">
                        <img :src="'./assets/images/no-results.png'" alt="No search results" class="img mb-4"/>
                      </div>
                      <h4>No Results To Show</h4>
                      <p>Sorry, we could not find any results that match your search or filter criteria. Please try using different keywords or filters.</p>
                      <button v-if="hasActiveFilters" @click="clearFilters" class="btn btn-primary mt-3">
                        <i class="bx bx-refresh me-2"></i>Clear Filters
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Pagination -->
                <div class="card-footer bg-white">
                  <nav aria-label="Page navigation" class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                      <span class="pr-3 pb-0 mb-0 font-13px">
                        <span>Rows Per Page</span>
                      </span>
                      <select class="form-select w-auto font-13px" v-model="pageSize">
                        <option value="10">10 Rows</option>
                        <option value="25">25 Rows</option>
                        <option value="50">50 Rows</option>
                        <option value="100">100 Rows</option>
                        <option value="150">150 Rows</option>
                        <option value="200">200 Rows</option>
                        <option value="1000">1000 Rows</option>
                        <option value="10000000000000">All Rows</option>
                      </select>
                    </div>

                    <ul class="pagination">
                      <li :class="{ disabled: currentPage === 1 }" class="page-item">
                        <a :disabled="currentPage === 1" @click="prevPage" class="page-link" tabindex="-1">Previous</a>
                      </li>
                      <li :class="{ active: currentPage === page }" :key="page" class="page-item" v-for="page in pagination(currentPage, totalPages, 3)">
                        <span class="page-link" v-if="page === '...'">{{ page }}</span>
                        <a @click="goToPage(page)" class="page-link" v-else>{{ page }}</a>
                      </li>
                      <li :class="{ disabled: currentPage === totalPages }" class="page-item">
                        <a :disabled="currentPage === totalPages" @click="nextPage" class="page-link">Next</a>
                      </li>
                    </ul>
                  </nav>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Download Progress Modal -->
    <div class="modal fade" id="downloadProgressModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-primary text-white">
            <h5 class="modal-title text-white">
              <i class="bx bxs-download me-2"></i>Downloading Business Records
            </h5>
          </div>
          <div class="modal-body">
            <div class="text-center mb-3">
              <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;" role="status" v-if="!downloadPaused">
                <span class="visually-hidden">Loading...</span>
              </div>
              <div v-else class="mb-3">
                <i class="bx bx-pause-circle text-warning" style="font-size: 3rem;"></i>
              </div>
              <h5 v-if="!downloadPaused">Please wait while we download your records...</h5>
              <h5 v-else class="text-warning">Download Paused</h5>

              <!-- Estimated Time Remaining -->
              <div v-if="downloadProgress.estimatedTimeRemaining && !downloadPaused" class="mt-2">
                <p class="text-muted mb-1">
                  <i class="bx bx-time-five me-1"></i>
                  <strong>{{ formatTimeRemaining(downloadProgress.estimatedTimeRemaining) }}</strong> remaining
                </p>
                <small class="text-muted">
                  Average speed: {{ downloadProgress.averageSpeed }} records/sec
                </small>
              </div>

              <p class="text-muted mt-2">{{ downloadPaused ? 'You can resume or cancel the download.' : 'This may take several minutes depending on the amount of data.' }}</p>
            </div>

            <div class="progress mb-3" style="height: 25px;">
              <div
                  class="progress-bar progress-bar-striped"
                  :class="{'progress-bar-animated': !downloadPaused, 'bg-warning': downloadPaused}"
                  role="progressbar"
                  :style="{ width: downloadProgress.percentage + '%' }"
                  :aria-valuenow="downloadProgress.percentage"
                  aria-valuemin="0"
                  aria-valuemax="100">
                {{ downloadProgress.percentage }}%
              </div>
            </div>

            <div class="d-flex justify-content-between text-muted small mb-3">
              <span>Page {{ downloadProgress.current }} of {{ downloadProgress.total }}</span>
              <span>{{ numberFormat(downloadProgress.records) }} / {{ numberFormat(downloadProgress.totalRecords) }} records</span>
            </div>

            <!-- Error Display -->
            <div v-if="downloadError" class="alert alert-danger d-flex align-items-center" role="alert">
              <i class="bx bx-error-circle me-2"></i>
              <div class="flex-grow-1">
                <strong>Error:</strong> {{ downloadError }}
                <br>
                <small>You can retry from where it stopped or cancel the download.</small>
              </div>
            </div>

            <!-- Retry Count Display -->
            <div v-if="downloadRetryCount > 0" class="alert alert-warning d-flex align-items-center mb-3" role="alert">
              <i class="bx bx-info-circle me-2"></i>
              <small>Retry attempt {{ downloadRetryCount }} of 3</small>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex gap-2 justify-content-center mb-3">
              <button
                  v-if="!downloadPaused && !downloadError"
                  @click="pauseDownload"
                  class="btn btn-warning btn-sm">
                <i class="bx bx-pause me-1"></i>Pause
              </button>
              <button
                  v-if="downloadPaused || downloadError"
                  @click="resumeDownload"
                  class="btn btn-success btn-sm">
                <i class="bx bx-play me-1"></i>Resume
              </button>
              <button
                  @click="cancelDownload"
                  class="btn btn-danger btn-sm">
                <i class="bx bx-x me-1"></i>Cancel
              </button>
            </div>

            <div class="alert alert-info d-flex align-items-center" role="alert">
              <i class="bx bx-info-circle me-2"></i>
              <small>{{ downloadPaused ? 'Download is paused. Your progress has been saved.' : 'Please do not close this window or navigate away.' }}</small>
            </div>

            <!-- Download Statistics -->
            <div v-if="downloadProgress.records > 0" class="mt-3 pt-3 border-top">
              <div class="row text-center">
                <div class="col-4">
                  <small class="text-muted d-block">Downloaded</small>
                  <strong>{{ numberFormat(downloadProgress.records) }}</strong>
                </div>
                <div class="col-4">
                  <small class="text-muted d-block">Remaining</small>
                  <strong>{{ numberFormat(downloadProgress.totalRecords - downloadProgress.records) }}</strong>
                </div>
                <div class="col-4">
                  <small class="text-muted d-block">Success Rate</small>
                  <strong>{{ getSuccessRate() }}%</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footer class="footer">
      <div class="container-fluid">
        <div class="row">
          <div class="col-sm-6"></div>
          <div class="col-sm-6">
            <div class="text-sm-end d-sm-block">
              County Government
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>

  <!-- Include the DuplicateManagerModal component -->
  <DuplicateManagerModal
      ref="duplicateManager"
      @show-message="showMessage"
      @refresh-data="refreshBusinessData"
      @edit-business="editBusiness"
  />

  <!-- Include the BusinessEntriesModal component -->
  <BusinessEntriesModal
      ref="businessEntriesModal"
      @show-message="showMessage"
  />

  <!-- Simple message display -->
  <div v-if="message" :class="getMessageClass()" class="alert alert-dismissible fade show" role="alert">
    <i :class="getMessageIcon()"></i>
    {{ message }}
    <button type="button" class="btn-close" @click="clearMessage" aria-label="Close"></button>
  </div>

  <!-- Right bar overlay-->
  <div class="rightbar-overlay"></div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import ToolBar from "@/components/sbp/ToolBar";
import DuplicateManagerModal from "@/components/sbp/duplicate/DuplicateManagerModal";
import BusinessEntriesModal from "@/components/sbp/modals/BusinessEntriesModal";
import router from "@/router";
import {authUrl, biller, execute, execute2, getRight, trade} from "@/api";
import moment from 'moment'

export default {
  name: "BusinessRegister",
  components: {
    ToolBar,
    NavigationBar,
    DuplicateManagerModal,
    BusinessEntriesModal
  },

  data() {
    return {
      search: '',
      loading: false,
      currentPage: 1,
      totalPages: 0,
      pageSize: 10,
      totalAmount: 0,
      totalItems: 0,
      dateFrom: '',
      dateTo: '',
      message: '',
      messageType: 'info',

      // Core data arrays
      businesses: [],
      subCounties: [],
      wards: [],
      zones: [],
      tradeCategories: [],
      tradeSubCategories: [],
      businessDesOptions: [],

      // Selected values for location filters
      selectedSubCounty: '',
      selectedWard: '',
      selectedZone: '',
      selectedBusinessDes: '',

      // Legacy selected values
      subCounty: {},
      ward: {},

      // Download functionality
      isDownloading: false,
      isDownloadingAll: false,
      downloadPaused: false,
      downloadCancelled: false,
      downloadError: null,
      downloadRetryCount: 0,
      maxRetries: 3,
      downloadProgress: {
        current: 0,
        total: 0,
        percentage: 0,
        records: 0,
        totalRecords: 0,
        startTime: null,
        estimatedTimeRemaining: null,
        averageSpeed: 0
      },
      allBusinessData: [],
      downloadState: {
        startPage: 1,
        currentPage: 0,
        batchSize: 1000,
        totalPages: 0,
        accumulatedData: []
      }
    }
  },

  computed: {
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    endIndex() {
      return this.currentPage * this.pageSize;
    },

    uniqueSubCounties() {
      const map = new Map();
      this.subCounties.forEach(item => {
        if (!map.has(item.subCountyID)) {
          map.set(item.subCountyID, {
            subCountyID: item.subCountyID,
            subCountyName: item.subCountyName
          });
        }
      });
      return Array.from(map.values());
    },

    filteredWards() {
      if (!this.selectedSubCounty) return [];
      return this.wards.filter(w => w.subCountyID === this.selectedSubCounty);
    },

    filteredZones() {
      if (!this.selectedWard) return [];
      return this.zones.filter(z => z.wardId === this.selectedWard);
    },

    hasActiveFilters() {
      return !!(this.selectedSubCounty || this.selectedWard || this.selectedZone || this.selectedBusinessDes);
    }
  },

  watch: {
    pageSize(newPageSize) {
      this.getBusinessRegistry("")
      const totalItems = this.totalItems;
      const newTotalPages = Math.ceil(totalItems / newPageSize);
      this.totalPages = newTotalPages;
      this.currentPage = 1;
    },
    search() {
      this.getBusinessRegistry(this.search)
    }
  },

  mounted() {
    this.dateFrom = moment().startOf('month').format('YYYY-MM-DD')
    this.dateTo = moment().format('YYYY-MM-DD')

    this.getBusinessRegistry("")
    this.getTradeCategory()
    this.getSubCountiesAndWards()
    this.getBusinessDesOptions()

    this.$watch('dateFrom', function () {
      if (this.dateFrom && this.dateTo) {
        this.getBusinessRegistry("")
      }
    })

    this.$watch('dateTo', function () {
      if (this.dateFrom && this.dateTo) {
        this.getBusinessRegistry("")
      }
    })

    this.menuAccess()
  },

  methods: {
    getRight(type) {
      return getRight(type)
    },

    // Location filter methods
    getSubCountiesAndWards() {
      const data = new FormData();
      data.append("function", "getSubCounties");

      execute2(data, biller)
          .then((res) => {
            if (res.data.success) {
              const rawData = res.data.data.subCounties;
              this.subCounties = rawData;

              const wardMap = new Map();
              rawData.forEach(item => {
                if (!wardMap.has(item.wardID)) {
                  wardMap.set(item.wardID, {
                    wardID: item.wardID,
                    wardName: item.wardName,
                    subCountyID: item.subCountyID,
                    subCountyName: item.subCountyName
                  });
                }
              });
              this.wards = Array.from(wardMap.values());
            }
          })
          .catch((e) => {
            console.error("Error loading subcounties:", e);
          });
    },

    loadZones(subCountyID, wardID) {
      const data = new FormData();
      data.append("function", "getZones");
      if (subCountyID) data.append("subCountyID", subCountyID);
      if (wardID) data.append("wardID", wardID);

      execute2(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.zones = (res.data.data.zones || []).map(zone => ({
                id: zone.id,
                name: zone.zone,
                wardId: zone.wardID,
                subCountyId: zone.subCountyID
              }));
            } else {
              this.zones = [];
            }
          })
          .catch(() => {
            this.zones = [];
          });
    },

    getBusinessDesOptions() {
      const data = new FormData();
      data.append("function", "getBusinessDesOptions");

      execute(data, trade)
          .then((res) => {
            if (res.data.success) {
              this.businessDesOptions = res.data.data.businessDesOptions || [];
            }
          })
          .catch((e) => {
            console.error("Error loading business types:", e);
          });
    },

    onSubCountyChange() {
      this.selectedWard = '';
      this.selectedZone = '';
      this.zones = [];
      if (this.selectedSubCounty) {
        this.loadZones(this.selectedSubCounty, null);
      }
      this.getBusinessRegistry(this.search);
    },

    onWardChange() {
      this.selectedZone = '';
      if (this.selectedWard) {
        this.loadZones(this.selectedSubCounty, this.selectedWard);
      } else {
        this.zones = [];
      }
      this.getBusinessRegistry(this.search);
    },

    onZoneChange() {
      this.getBusinessRegistry(this.search);
    },

    onBusinessDesChange() {
      this.getBusinessRegistry(this.search);
    },

    clearFilters() {
      this.selectedSubCounty = '';
      this.selectedWard = '';
      this.selectedZone = '';
      this.selectedBusinessDes = '';
      this.zones = [];
      this.getBusinessRegistry(this.search);
    },

    getSubCountyName(id) {
      const subCounty = this.uniqueSubCounties.find(s => s.subCountyID === id);
      return subCounty ? subCounty.subCountyName : id;
    },

    getWardName(id) {
      const ward = this.wards.find(w => w.wardID === id);
      return ward ? ward.wardName : id;
    },

    getZoneName(id) {
      const zone = this.zones.find(z => z.id === id);
      return zone ? zone.name : id;
    },

    openDuplicateManager() {
      this.$refs.duplicateManager.openModal();
    },

    viewBusinessEntries(business) {
      this.$refs.businessEntriesModal.showModal(business);
    },

    refreshBusinessData() {
      this.getBusinessRegistry(this.search);
    },

    editBusiness(business) {
      console.log('Edit business:', business);
    },

    showMessage(message, type = 'info') {
      this.message = message;
      this.messageType = type;
      setTimeout(() => {
        this.clearMessage();
      }, 5000);
    },

    clearMessage() {
      this.message = '';
      this.messageType = 'info';
    },

    getMessageClass() {
      const classes = {
        'success': 'alert-success',
        'error': 'alert-danger',
        'warning': 'alert-warning',
        'info': 'alert-info'
      };
      return classes[this.messageType] || 'alert-info';
    },

    getMessageIcon() {
      const icons = {
        'success': 'bx bx-check-circle me-2',
        'error': 'bx bx-error-circle me-2',
        'warning': 'bx bx-error me-2',
        'info': 'bx bx-info-circle me-2'
      };
      return icons[this.messageType] || 'bx bx-info-circle me-2';
    },

    // Pagination
    pagination(currentPage, totalPages, maxVisibleButtons) {
      let pages = [];
      let start = currentPage - Math.floor(maxVisibleButtons / 2);
      let end = currentPage + Math.floor(maxVisibleButtons / 2);

      if (start < 1) {
        start = 1;
        end = start + maxVisibleButtons - 1;
        if (end > totalPages) end = totalPages;
      }
      if (end > totalPages) {
        end = totalPages;
        start = end - maxVisibleButtons + 1;
        if (start < 1) start = 1;
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (start > 1) pages.unshift("...");
      if (end < totalPages) pages.push("...");

      return pages;
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getBusinessRegistry("")
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getBusinessRegistry("")
      }
    },

    goToPage(page) {
      if (page > 0 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.getBusinessRegistry("")
      }
    },

    // Utilities
    numberFormat(num) {
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(num)
    },

    formattedDate(date) {
      return moment(date).format('DD MMM YYYY hh:mm A') + ' (' + moment(date).fromNow() + ')'
    },

    menuAccess() {
      const data = new FormData();
      data.append("function", "menuAccess");
      data.append("menu", "Approved Biz");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, biller)
          .then(() => {})
          .catch(() => {});
    },

    // Get Business Registry
    getBusinessRegistry(search) {
      this.loading = true
      this.businesses = []

      const data = new FormData();
      data.append("function", "getBusinessRegistry")
      data.append("search", search)
      data.append("dateFrom", this.dateFrom)
      data.append("dateTo", this.dateTo)
      data.append("page", this.currentPage);
      data.append("rows_per_page", this.pageSize);

      if (this.selectedSubCounty) data.append("filterSubCountyID", this.selectedSubCounty);
      if (this.selectedWard) data.append("filterWardID", this.selectedWard);
      if (this.selectedZone) {
        const zoneName = this.getZoneName(this.selectedZone);
        data.append("filterZone", zoneName);
      }
      if (this.selectedBusinessDes) data.append("filterBusinessDes", this.selectedBusinessDes);

      execute(data, trade)
          .then((res) => {
            this.loading = false
            if (res.data.success) {
              this.businesses = res.data.data.businesses
              this.totalItems = res.data.data.count
              this.totalPages = Math.ceil(this.totalItems / this.pageSize);

              this.totalAmount = this.businesses.reduce((acc, cur) => {
                let totalAmount = Number(cur.receiptAmount || 0);
                return !isNaN(totalAmount) ? acc + totalAmount : acc;
              }, 0);
            } else {
              this.showMessage(res.data.message, 'error');
            }
          })
          .catch((e) => {
            this.loading = false
            this.showMessage('Error loading business registry: ' + e.message, 'error');
          });
    },

    getSubCounties() {
      const data = new FormData();
      data.append("function", "getSubCounties");
      execute(data, authUrl)
          .then((res) => {
            if (!res.data.success) {
              this.showMessage(res.data.message, 'error');
            }
          })
          .catch((e) => {
            this.showMessage(e.message, 'error');
          });
    },

    getWards(subCountyID) {
      const data = new FormData();
      data.append("function", "getWards");
      data.append("subCountyID", subCountyID);
      execute(data, authUrl)
          .then((res) => {
            if (!res.data.success) {
              this.showMessage(res.data.message, 'error');
            }
          })
          .catch((e) => {
            this.showMessage(e.message, 'error');
          });
    },

    getTradeCategory() {
      const data = new FormData();
      data.append("function", "getIncomeTypes");
      data.append("incomeTypePrefix", "SBP");
      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.tradeCategories = res.data.data.incomeTypes
            } else {
              this.showMessage(res.data.message, 'error');
            }
          })
          .catch((e) => {
            this.showMessage(e.message, 'error');
          });
    },

    getTradeSubCategory(event) {
      this.loading = true
      this.getBusinessRegistry(event.target.value)
      this.tradeSubCategories = []

      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", event.target.value);
      execute(data, biller)
          .then((res) => {
            this.loading = false
            if (res.data.success) {
              this.tradeSubCategories = res.data.data.feesAndCharges
            } else {
              this.showMessage(res.data.message, 'error');
            }
          })
          .catch((e) => {
            this.loading = false
            this.showMessage(e.message, 'error');
          });
    },

    selectedTradeSubCategory(event) {
      const data = JSON.parse(event.target.value)
      this.getBusinessRegistry(data.feeId)
    },

    // Generate CSV — all fields
    generateCSV(data) {
      if (!data || data.length === 0) return '';

      const headers = [
        'NO.',
        'Business ID',
        'Business Name',
        'Category',
        'Sub Category',
        'Business Type',
        'Owner / Full Names',
        'Sub County',
        'Ward',
        'Zone',
        'Phone',
        'Email',
        'Physical Address',
        'Plot Number',
        'Postal Address',
        'Postal Code',
        'Latitude',
        'Longitude',
        'Fee ID',
        'Income Type ID',
        'Created By',
        'Date Created'
      ];

      let csv = headers.join(',') + '\n';

      data.forEach((item, index) => {
        const row = [
          index + 1,
          this.escapeCSV(item.businessID),
          this.escapeCSV(item.businessName),
          this.escapeCSV(item.businessCategory),
          this.escapeCSV(item.businessSubCategory),
          this.escapeCSV(item.businessDes || ''),
          this.escapeCSV(item.fullnames || ''),
          this.escapeCSV(item.subCountyName),
          this.escapeCSV(item.wardName),
          this.escapeCSV(item.zone || ''),
          this.escapeCSV(item.contactPersonPhone || ''),
          this.escapeCSV(item.businessEmail || ''),
          this.escapeCSV(item.physicalAddress || ''),
          this.escapeCSV(item.plotNumber || ''),
          this.escapeCSV(item.postalAddress || ''),
          this.escapeCSV(item.postalCode || ''),
          this.escapeCSV(item.lat || ''),
          this.escapeCSV(item.lng || ''),
          this.escapeCSV(item.feeID || ''),
          this.escapeCSV(item.incomeTypeID || ''),
          this.escapeCSV(item.createdBy),
          this.escapeCSV(this.formattedDate(item.dateCreated))
        ];
        csv += row.join(',') + '\n';
      });

      // Append active filter metadata
      csv += '\nFILTERS APPLIED\n';
      if (this.selectedSubCounty) csv += `Sub County:,${this.escapeCSV(this.getSubCountyName(this.selectedSubCounty))}\n`;
      if (this.selectedWard) csv += `Ward:,${this.escapeCSV(this.getWardName(this.selectedWard))}\n`;
      if (this.selectedZone) csv += `Zone:,${this.escapeCSV(this.getZoneName(this.selectedZone))}\n`;
      if (this.selectedBusinessDes) csv += `Business Type:,${this.escapeCSV(this.selectedBusinessDes)}\n`;

      return csv;
    },

    escapeCSV(value) {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return '"' + stringValue.replace(/"/g, '""') + '"';
      }
      return stringValue;
    },

    getSuccessRate() {
      if (this.downloadProgress.total === 0) return 100;
      const successfulPages = this.downloadProgress.current - this.downloadRetryCount;
      return Math.round((successfulPages / this.downloadProgress.total) * 100);
    },

    // Download Current Page
    downloadCurrentPage() {
      if (this.businesses.length === 0) {
        this.showMessage('No data available to download', 'warning');
        return;
      }

      this.isDownloading = true;

      try {
        const csv = this.generateCSV(this.businesses);
        const blob = new Blob(['\ufeff' + csv], {type: 'text/csv;charset=utf-8;'});
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);

        let filename = `Business_Registry_Page_${this.currentPage}_${moment().format('YYYY-MM-DD_HHmmss')}`;
        if (this.selectedSubCounty) filename += `_${this.getSubCountyName(this.selectedSubCounty).replace(/\s+/g, '_')}`;
        if (this.selectedWard) filename += `_${this.getWardName(this.selectedWard).replace(/\s+/g, '_')}`;
        if (this.selectedZone) filename += `_${this.getZoneName(this.selectedZone).replace(/\s+/g, '_')}`;
        filename += '.csv';

        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.showMessage(`Successfully downloaded ${this.businesses.length} records from current page`, 'success');
      } catch (error) {
        console.error('Download error:', error);
        this.showMessage('Failed to download data: ' + error.message, 'error');
      } finally {
        this.isDownloading = false;
      }
    },

    // Format time remaining
    formatTimeRemaining(seconds) {
      if (!seconds || seconds <= 0) return 'Calculating...';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);
      if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
      if (minutes > 0) return `${minutes}m ${secs}s`;
      return `${secs}s`;
    },

    calculateEstimatedTime() {
      if (!this.downloadProgress.startTime || this.downloadProgress.current === 0) return;

      const elapsedTime = (Date.now() - this.downloadProgress.startTime) / 1000;
      const recordsDownloaded = this.downloadProgress.records;
      const remainingRecords = this.downloadProgress.totalRecords - recordsDownloaded;

      if (recordsDownloaded > 0 && elapsedTime > 0) {
        const averageSpeed = recordsDownloaded / elapsedTime;
        this.downloadProgress.averageSpeed = averageSpeed.toFixed(2);
        this.downloadProgress.estimatedTimeRemaining = remainingRecords / averageSpeed;
      }
    },

    pauseDownload() {
      this.downloadPaused = true;
    },

    async resumeDownload() {
      this.downloadPaused = false;
      this.downloadError = null;
      this.downloadRetryCount = 0;
      await this.continueDownload();
    },

    cancelDownload() {
      const confirmed = confirm('Are you sure you want to cancel the download? All progress will be lost.');
      if (confirmed) {
        this.downloadCancelled = true;
        this.closeDownloadModal();
      }
    },

    closeDownloadModal() {
      const modalElement = document.getElementById('downloadProgressModal');
      if (modalElement) {
        const existingModal = bootstrap.Modal.getInstance(modalElement);
        if (existingModal) existingModal.hide();
      }

      document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';

      this.isDownloadingAll = false;
      this.downloadPaused = false;
      this.downloadCancelled = false;
      this.downloadError = null;
      this.downloadRetryCount = 0;
      this.allBusinessData = [];
      this.downloadProgress = {
        current: 0, total: 0, percentage: 0,
        records: 0, totalRecords: 0, startTime: null,
        estimatedTimeRemaining: null, averageSpeed: 0
      };
      this.downloadState = {
        startPage: 1, currentPage: 0,
        batchSize: 1000, totalPages: 0, accumulatedData: []
      };
    },

    // Download All Records
    async downloadAllRecords() {
      if (this.totalItems === 0) {
        this.showMessage('No records to download', 'warning');
        return;
      }

      const isConfirmed = confirm(
          `You are about to download all ${this.numberFormat(this.totalItems)} business records. This may take several minutes. Do you want to continue?`
      );
      if (!isConfirmed) return;

      this.isDownloadingAll = true;
      this.downloadPaused = false;
      this.downloadCancelled = false;
      this.downloadError = null;
      this.downloadRetryCount = 0;
      this.allBusinessData = [];

      const batchSize = 1000;
      const totalRecords = this.totalItems;
      const totalPages = Math.ceil(totalRecords / batchSize);

      this.downloadState = {
        startPage: 1, currentPage: 0,
        batchSize, totalPages, accumulatedData: []
      };

      this.downloadProgress = {
        current: 0, total: totalPages, percentage: 0,
        records: 0, totalRecords, startTime: Date.now(),
        estimatedTimeRemaining: null, averageSpeed: 0
      };

      const modalElement = document.getElementById('downloadProgressModal');
      const progressModal = new bootstrap.Modal(modalElement);
      progressModal.show();

      await this.continueDownload();
    },

    async continueDownload() {
      try {
        const startPage = this.downloadState.currentPage + 1;
        const totalPages = this.downloadState.totalPages;

        for (let page = startPage; page <= totalPages; page++) {
          if (this.downloadPaused) return;
          if (this.downloadCancelled) return;

          try {
            this.downloadState.currentPage = page;
            this.downloadProgress.current = page;
            this.downloadProgress.percentage = Math.round((page / totalPages) * 100);

            const pageData = await this.fetchBusinessPageWithRetry(page, this.downloadState.batchSize);

            if (pageData && pageData.length > 0) {
              this.downloadState.accumulatedData = [...this.downloadState.accumulatedData, ...pageData];
              this.allBusinessData = this.downloadState.accumulatedData;
              this.downloadProgress.records = this.allBusinessData.length;
              this.calculateEstimatedTime();
            }

            this.downloadRetryCount = 0;
            this.downloadError = null;

            await this.delay(50);

          } catch (error) {
            console.error(`Error fetching page ${page}:`, error);
            this.downloadError = error.message;
            this.downloadPaused = true;
            return;
          }
        }

        await this.finalizeDownload();

      } catch (error) {
        console.error('Download error:', error);
        this.downloadError = error.message;
        this.downloadPaused = true;
      }
    },

    async fetchBusinessPageWithRetry(page, rowsPerPage, retryCount = 0) {
      try {
        const data = new FormData();
        data.append("function", "getBusinessRegistry");
        data.append("page", page);
        data.append("rows_per_page", rowsPerPage);
        data.append("search", this.search || '');
        data.append("dateFrom", this.dateFrom || '');
        data.append("dateTo", this.dateTo || '');

        if (this.selectedSubCounty) data.append("filterSubCountyID", this.selectedSubCounty);
        if (this.selectedWard) data.append("filterWardID", this.selectedWard);
        if (this.selectedZone) {
          const zoneName = this.getZoneName(this.selectedZone);
          data.append("filterZone", zoneName);
        }
        if (this.selectedBusinessDes) data.append("filterBusinessDes", this.selectedBusinessDes);

        const res = await execute(data, trade);

        if (res.data.success && res.data.data.businesses) {
          return res.data.data.businesses;
        }
        return [];
      } catch (error) {
        if (retryCount < this.maxRetries) {
          this.downloadRetryCount = retryCount + 1;
          await this.delay(1000 * (retryCount + 1));
          return await this.fetchBusinessPageWithRetry(page, rowsPerPage, retryCount + 1);
        } else {
          throw error;
        }
      }
    },

    async finalizeDownload() {
      try {
        if (this.allBusinessData.length === 0) {
          this.showMessage('No business data to download', 'warning');
          this.closeDownloadModal();
          return;
        }

        const csv = this.generateCSV(this.allBusinessData);
        const blob = new Blob(['\ufeff' + csv], {type: 'text/csv;charset=utf-8;'});
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);

        let filename = `All_Business_Registry_${moment().format('YYYY-MM-DD_HHmmss')}`;
        if (this.selectedSubCounty) filename += `_${this.getSubCountyName(this.selectedSubCounty).replace(/\s+/g, '_')}`;
        if (this.selectedWard) filename += `_${this.getWardName(this.selectedWard).replace(/\s+/g, '_')}`;
        if (this.selectedZone) filename += `_${this.getZoneName(this.selectedZone).replace(/\s+/g, '_')}`;
        filename += '.csv';

        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.showMessage(`Successfully downloaded ${this.numberFormat(this.allBusinessData.length)} business records`, 'success');
        this.closeDownloadModal();

      } catch (error) {
        console.error('Finalize download error:', error);
        this.showMessage('Error finalizing download: ' + error.message, 'error');
        this.closeDownloadModal();
      }
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
  }
}
</script>

<style scoped>
.alert {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1060;
  min-width: 300px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.2rem;
}

.text-center {
  text-align: center;
}

.gap-2 {
  gap: 0.5rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

/* Filter badges */
.badge.bg-light {
  border: 1px solid #dee2e6;
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
}
</style>